import { buildLesson } from './grammar/topics';
import { termKey, type Card } from './cards';
import { translate } from './translate';
import type { Assignment } from './types';
import type { QuizQuestion } from './quiz';

// ─────────────────────────────────────────────────────────────────────────────
// Questions for the class game, from this week's work:
//   grammar  multiple-choice questions from the week's grammar lessons (typed
//            ones get their choices from the other answers)
//   story    short phrases from the week's stories: pick the right translation
//            (translated on the teacher's page, the same way students' taps are;
//            nothing is saved)
//   vocab    words EVERY student who saved words this week saved: what do they mean?
// ─────────────────────────────────────────────────────────────────────────────

const shuffle = <T,>(a: T[]) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };

/** Build a question from a right answer and some others to choose from (four at most, all different). */
function withChoices(base: Omit<QuizQuestion, 'options' | 'correct'>, right: string, pool: string[]): QuizQuestion | null {
  const others = shuffle([...new Set(pool.filter(p => p.trim() && p.trim().toLowerCase() !== right.trim().toLowerCase()))]).slice(0, 3);
  if (others.length < 1) return null;
  const options = shuffle([right, ...others]);
  return { ...base, options, correct: options.indexOf(right) };
}

export function grammarQuestions(week: Assignment[]): QuizQuestion[] {
  const out: QuizQuestion[] = [];
  for (const a of week.filter(x => x.kind === 'grammar' && x.grammar)) {
    const qs = buildLesson({ ...a.grammar!, length: 20 }, 2);
    const typedAnswers = qs.filter(q => q.kind === 'type').map(q => q.answers[0]);
    for (const q of qs) {
      if (q.kind === 'order' || q.kind === 'match') continue;
      const base = { source: 'grammar' as const, instruction: q.instruction, prompt: q.prompt, hint: q.hint };
      const made = q.kind === 'choice' && q.options?.length
        ? withChoices(base, q.answers[0], q.options)
        : withChoices(base, q.answers[0], typedAnswers);
      if (made) out.push(made);
    }
  }
  return dedupe(out);
}

/** Short phrases from a story: whole short sentences, or the parts of longer ones between commas. */
export function storyPhrases(text: string): string[] {
  const words = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;
  const pieces: string[] = [];
  for (const sentence of text.replace(/\s+/g, ' ').split(/(?<=[.!?…])\s+/)) {
    const s = sentence.trim();
    if (!s) continue;
    if (words(s) >= 3 && words(s) <= 10) { pieces.push(s); continue; }
    for (const part of s.split(/[,;:—–]\s*/)) if (words(part) >= 3 && words(part) <= 9) pieces.push(part.trim());
  }
  return [...new Set(pieces)];
}

export async function storyQuestions(week: Assignment[], source: string, target: string, howMany = 10): Promise<QuizQuestion[]> {
  const phrases = shuffle(week.filter(a => a.kind === 'story' && a.story_text).flatMap(a => storyPhrases(a.story_text!))).slice(0, howMany + 3);
  if (phrases.length < 2) return [];
  const done = await Promise.allSettled(phrases.map(p => translate(p, source, target)));
  const pairs = phrases.map((p, i) => ({ p, t: done[i].status === 'fulfilled' ? (done[i] as PromiseFulfilledResult<{ text: string }>).value.text : '' })).filter(x => x.t);
  const all = pairs.map(x => x.t);
  return pairs.slice(0, howMany)
    .map(x => withChoices({ source: 'story', instruction: 'Which is the right translation?', prompt: x.p, lang: source }, x.t, all))
    .filter((q): q is QuizQuestion => !!q);
}

/**
 * Words every student who saved anything this week saved (at least two
 * students). Their meaning is the one most of them saved; the wrong choices
 * come from the rest of the class's cards.
 */
export function sharedVocab(cards: Card[], weekStart: string, classroomId: string): { questions: QuizQuestion[]; students: number } {
  const since = new Date(`${weekStart}T00:00:00`).getTime();
  const week = cards.filter(c => c.kind === 'word' && (c.classroom_id === classroomId || !c.classroom_id) && new Date(c.created_at).getTime() >= since);
  const byStudent = new Map<string, Card[]>();
  for (const c of week) byStudent.set(c.student_id, [...(byStudent.get(c.student_id) ?? []), c]);
  if (byStudent.size < 2) return { questions: [], students: byStudent.size };
  const sets = [...byStudent.values()].map(cs => new Set(cs.map(c => termKey(c.term))));
  const shared = [...sets[0]].filter(k => sets.every(s => s.has(k)));
  const pool = [...new Set(cards.map(c => c.translation))];
  const questions = shared.map(k => {
    const mine = week.filter(c => termKey(c.term) === k);
    const tally = new Map<string, number>();
    for (const c of mine) tally.set(c.translation, (tally.get(c.translation) ?? 0) + 1);
    const meaning = [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
    return withChoices({ source: 'vocab', instruction: 'Everyone saved this word. What does it mean?', prompt: mine[0].term, lang: mine[0].source_lang }, meaning, pool);
  }).filter((q): q is QuizQuestion => !!q);
  return { questions, students: byStudent.size };
}

function dedupe(qs: QuizQuestion[]): QuizQuestion[] {
  const seen = new Set<string>();
  return qs.filter(q => { const k = `${q.prompt}|${q.options[q.correct]}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

/** Take turns from each source chosen, so a game mixes them; `total` at most. */
export function mixQuestions(lists: QuizQuestion[][], total: number): QuizQuestion[] {
  const pools = lists.map(l => shuffle(l)).filter(l => l.length);
  const out: QuizQuestion[] = [];
  while (out.length < total && pools.some(p => p.length)) for (const p of pools) { const q = p.shift(); if (q && out.length < total) out.push(q); }
  return out;
}
